import { LegacyPluginRedirect } from '@/components/LegacyPluginRedirect';
import { TabFallback } from '@/components/TabFallback';
import { PluginCrumb } from '@/components/breadcrumbs/PluginCrumb';
import { Suspense, lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import type { RouteHandle } from './types';

// G3-T13: catalog reads `/api/plugins` (authenticated) rather than the
// anonymous `/.well-known/marketplace.json` manifest. Plugin detail is nested
// here as `/catalog/:slug` so the breadcrumb chain (useMatches) yields the
// "Catalog" parent crumb — the URL IS the hierarchy.
const Catalog = lazy(() => import('@/pages/Catalog').then((m) => ({ default: m.Catalog })));
const PluginDetail = lazy(() =>
  import('@/pages/PluginDetail').then((m) => ({ default: m.PluginDetail })),
);

const listHandle: RouteHandle = {
  // `plugins:view` is granted to every role per Appendix A — effectively
  // any-authenticated, but stated explicitly so sidebar/command-palette/gate
  // agree. The index child inherits this (deepest-declared wins).
  crumb: 'Catalog',
  requiredPermissions: ['plugins:view'],
};

const detailHandle: RouteHandle = {
  crumb: (params) => <PluginCrumb slug={params.slug} />,
  requiredPermissions: ['plugins:view'],
};

export const catalogRoutes: RouteObject[] = [
  {
    path: 'catalog',
    handle: listHandle,
    // No `element` → react-router renders an implicit <Outlet/> for the
    // children below.
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<TabFallback />}>
            <Catalog />
          </Suspense>
        ),
      },
      {
        path: ':slug',
        handle: detailHandle,
        element: (
          <Suspense fallback={<TabFallback />}>
            <PluginDetail />
          </Suspense>
        ),
      },
    ],
  },
  // Legacy `/plugin/:slug` → `/catalog/:slug`. A real redirect route retiring
  // the old URL — not a pathless wrapper faking a "Catalog" ancestor.
  {
    path: 'plugin/:slug',
    element: <LegacyPluginRedirect />,
  },
];
